import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAccount } from "../../context/AccountContext";
import { useFeatureTheme } from "../../context/FeatureThemeContext";
import { useToast } from "../../context/ToastContext";
import { themeApi } from "../../services/userApi";
import { Card } from "../../components/common/Card";
import { Button } from "../../components/common/Button";
import { Badge } from "../../components/common/Badge";
import styles from "./ThemeSettingsTab.module.css";
import { THEME_COPY } from "./themeCopy";


/* The order the cards are shown in: simplest first, then the presets by
   how much of the operation they cover, with the two open-ended choices
   last. Used only when the server has not answered yet. */
const FALLBACK_THEMES = [
  { name: "Basic", min_tier: "Free", available: true },
  { name: "Custom", min_tier: "Free", available: true },
];

export function ThemeSettingsTab() {
  const { theme, refresh, themes, helperTheme } = useFeatureTheme();
  const { actingAccountId, currentLabel } = useAccount();
  const navigate = useNavigate();
  const { notify } = useToast();
  const [busy, setBusy] = useState(null);

  async function selectTheme(key) {
    setBusy(key);
    try {
      await themeApi.update(key);
      await refresh();
      notify(`Switched to ${key} theme`, "success");
    } catch (err) {
      notify(err.apiMessage || "Could not switch theme", "danger");
    } finally {
      setBusy(null);
    }
  }

  async function chooseHelperTheme(name) {
    setBusy("helper");
    try {
      await themeApi.setHelperTheme(name);
      await refresh();
      notify(name ? `Showing ${name} in this account` : "Showing everything this account has", "success");
    } catch (err) {
      notify(err.apiMessage || "Could not change what you see here", "danger");
    } finally {
      setBusy(null);
    }
  }

  /* Presets this ACCOUNT can reach -- `themes` is computed against the
     account's tier when acting in one. Basic/Advanced/Custom describe a
     person's own toggles and are not choices here (#508). */
  const helperOptions = (themes || []).filter(
    (o) => o.available && THEME_COPY[o.name] && !["Basic", "Advanced", "Custom"].includes(o.name));

  /* Only names this build has copy for. A preset added to the database
     ahead of a web release would otherwise render a card with no title
     and no description. */
  const options = (themes?.length ? themes : FALLBACK_THEMES).filter(
    (option) => THEME_COPY[option.name]
  );

  return (
    <>
      {actingAccountId && (
        <Card className={styles.card}>
          {/* A theme PER ACCOUNT you help in (#508). The default is
              everything the account has, so nobody is hidden from a screen
              unless they choose to be (#443). */}
          <h3>While helping in {currentLabel || "this account"}</h3>
          <p>
            Choose what you see here. This only changes your own view; what
            you are able to do is set by the account owner.
          </p>
          <label htmlFor="helper-theme">What to show</label>
          <select
            id="helper-theme"
            name="helper-theme"
            value={helperTheme || ""}
            disabled={busy === "helper"}
            onChange={(e) => chooseHelperTheme(e.target.value || null)}
          >
            <option value="">Everything this account has</option>
            {helperOptions.map((o) => (
              <option key={o.name} value={o.name}>{THEME_COPY[o.name].title}</option>
            ))}
          </select>
        </Card>
      )}
      {/* NO TESTING NOTICE HERE, decided 6 Oct (#492).
          Testers are working against dev directly and this screen should
          read exactly as it will after go-live, so the copy below is
          production copy and the "free during testing" caveat is gone.
          Tier changes are still free -- the behaviour has not moved, only
          what the screen claims about it. */}
    <div className={styles.grid}>
      {options.map((option) => {
        const copy = THEME_COPY[option.name];
        /*
         * WHETHER IT IS LOCKED IS THE SERVER'S ANSWER (#485).
         *
         * This used to be `t.key === "Advanced" && tierLabel !== "Business"`
         * -- correct while Advanced was the only preset that turned
         * anything on, and wrong the moment there were five. "Hive
         * Keeping" is six Harvester features; a client-side rule naming
         * Business would have locked a Harvester account out of a preset
         * built for it, and the server would have allowed it.
         *
         * #445's correction still holds and now holds by construction:
         * Basic and Custom come back with min_tier Free, so neither can
         * be locked by accident again.
         */
        const requiresUpgrade = option.available === false;
        const isCurrent = theme === option.name;
        return (
          <Card key={option.name} className={[styles.card, isCurrent ? styles.selected : ""].join(" ")}>
            <div className={styles.cardHeader}>
              <h3>{copy.title}</h3>
              {isCurrent && <Badge tone="honey">Current</Badge>}
            </div>
            <p>{copy.description}</p>
            {requiresUpgrade && (
              <p className={styles.note}>
                {/* The tier is NAMED, and named by the server rather than
                    guessed here -- the stale-vocabulary trap this line has
                    already been fixed for once, when it said "Premium
                    Standard or AI" after V047 removed both. */}
                {`Requires the ${option.min_tier} plan.`}
              </p>
            )}
            <Button
              variant={isCurrent ? "secondary" : "primary"}
              /*
               * A LOCKED THEME NO LONGER OFFERS "Select" (#492).
               *
               * Reported 6 Oct: "the 'Select' button is usable on all
               * cards and just gives an error for the ones that aren't
               * usable." It invited a press that could only fail, and the
               * failure arrived as a toast with no next step.
               *
               * So it says Upgrade and GOES somewhere -- the Subscription
               * tab, which is the thing that would actually unlock it.
               * Disabling it instead would be honest and useless: the
               * person pressing it wants the theme, and the tab they need
               * is one they have to find.
               */
              disabled={isCurrent || busy === option.name}
              onClick={() =>
                requiresUpgrade
                  ? navigate("/settings/subscription")
                  : selectTheme(option.name)
              }
            >
              {busy === option.name
                ? "Switching…"
                : isCurrent
                  ? "Selected"
                  : requiresUpgrade
                    ? "Upgrade"
                    : "Select"}
            </Button>
          </Card>
        );
      })}
    </div>
    </>
  );
}
